import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useCart } from "../context/CartContext";
import { ProductCard } from "../components/ProductCard";
import { Star, ShieldCheck, Minus, Plus, Truck, RotateCcw, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

export default function ProductDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [qty, setQty] = useState(1);

  useEffect(() => {
    setQty(1);
    api.get(`/products/${slug}`).then((r) => {
      setProduct(r.data);
      api.get(`/products?collection=${r.data.collection}`).then((rr) =>
        setRelated(rr.data.filter((p) => p.slug !== slug).slice(0, 4)));
    }).catch(() => navigate("/shop"));
  }, [slug, navigate]);

  if (!product) return <div className="text-center py-32 text-gray-400">Loading…</div>;

  const handleAdd = () => {
    addItem(product, qty);
    toast.success(`${product.name} added to cart`);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <button onClick={() => navigate(-1)} className="inline-flex items-center gap-2 text-sm font-semibold text-gray-500 hover:text-[#FF5C28] mb-8">
        <ArrowLeft className="w-4 h-4" /> Back
      </button>

      <div className="grid lg:grid-cols-2 gap-12">
        <div className="bg-[#F8F9FA] rounded-3xl p-8 flex items-center justify-center">
          <img src={product.image} alt={product.name} data-testid="product-detail-image" className="max-h-[480px] object-contain" />
        </div>

        <div>
          {product.badge && <span className="inline-block bg-[#FF5C28] text-white text-xs font-bold px-3 py-1 rounded-full mb-4">{product.badge}</span>}
          <h1 className="font-display font-black text-3xl md:text-4xl tracking-tight mb-3" data-testid="product-detail-name">{product.name}</h1>
          <div className="flex items-center gap-3 mb-5">
            <div className="flex items-center gap-1">
              {[...Array(5)].map((_, i) => <Star key={i} className={`w-4 h-4 ${i < Math.round(product.rating) ? "fill-[#FF5C28] text-[#FF5C28]" : "text-gray-300"}`} />)}
            </div>
            <span className="text-sm font-bold">{product.rating}</span>
            <span className="text-sm text-gray-400">({product.reviews_count} reviews)</span>
            {product.vet_reviewed && (
              <span className="inline-flex items-center gap-1 text-xs font-bold text-emerald-600 ml-2">
                <ShieldCheck className="w-4 h-4" /> Vet Reviewed
              </span>
            )}
          </div>
          <p className="font-display font-black text-3xl mb-6" data-testid="product-detail-price">{product.price === 0 ? "Free" : `$${product.price.toFixed(2)}`}</p>
          <p className="text-gray-600 leading-relaxed mb-8">{product.description}</p>

          <div className="flex items-center gap-4 mb-6">
            <div className="flex items-center bg-[#F8F9FA] rounded-full border border-gray-200">
              <button onClick={() => setQty(Math.max(1, qty - 1))} data-testid="qty-minus" className="p-3 hover:text-[#FF5C28]"><Minus className="w-4 h-4" /></button>
              <span className="w-10 text-center font-bold" data-testid="qty-value">{qty}</span>
              <button onClick={() => setQty(qty + 1)} data-testid="qty-plus" className="p-3 hover:text-[#FF5C28]"><Plus className="w-4 h-4" /></button>
            </div>
            <button onClick={handleAdd} data-testid="add-to-cart-btn"
              className="flex-1 rounded-full bg-[#FF5C28] text-white font-bold px-8 py-4 hover:bg-[#E04D1E] hover:-translate-y-0.5 transition-all shadow-lg shadow-[#FF5C28]/25">
              Add to Cart
            </button>
          </div>

          <div className="grid grid-cols-2 gap-4 border-t border-gray-100 pt-6">
            <div className="flex items-center gap-3 text-sm">
              <Truck className="w-5 h-5 text-[#FF5C28]" /> <span className="font-medium">Free shipping over $50</span>
            </div>
            <div className="flex items-center gap-3 text-sm">
              <RotateCcw className="w-5 h-5 text-[#FF5C28]" /> <span className="font-medium">30-day easy returns</span>
            </div>
          </div>
        </div>
      </div>

      {related.length > 0 && (
        <div className="mt-20">
          <h2 className="font-display font-extrabold text-3xl tracking-tight mb-8">You might also need</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
            {related.map((p) => <ProductCard key={p.slug} product={p} />)}
          </div>
        </div>
      )}
    </div>
  );
}
